/**
 * Prepared Turntable — viewer activation adapter (Stage 5C2c).
 *
 * Lazy only: call resolveViewerMedia() from explicit viewer open.
 * Does NOT fetch on card render / search / mapDiamondItem.
 * Does NOT fetch frame bodies — only adapts normalized manifest frames
 * into the existing Frame Engine media shape.
 *
 * On any prepared miss/failure, returns the original Nivoda v360 (or null).
 */
(function (global) {
  "use strict";

  /**
   * Convert a Stage 5C2b normalized prepared manifest into the media object
   * diamond-frame-engine resolveFrameMedia() already understands
   * (snake_case frame_count/top_index + absolute frames[].url).
   * Returns null when the normalized payload is not usable.
   */
  function normalizedManifestToMedia(normalized) {
    if (!normalized || normalized.available !== true) return null;
    if (!Array.isArray(normalized.frames) || normalized.frames.length === 0) {
      return null;
    }

    var frames = [];
    for (var i = 0; i < normalized.frames.length; i += 1) {
      var entry = normalized.frames[i];
      if (!entry || typeof entry.url !== "string" || entry.url.length === 0) {
        return null;
      }
      frames.push({
        url: entry.url,
        sourceIndex: entry.sourceIndex,
      });
    }

    var frameCount =
      typeof normalized.frameCount === "number" &&
      isFinite(normalized.frameCount) &&
      normalized.frameCount > 0
        ? Math.floor(normalized.frameCount)
        : frames.length;
    if (frameCount !== frames.length) return null;

    var topIndex =
      typeof normalized.topIndex === "number" && isFinite(normalized.topIndex)
        ? Math.floor(normalized.topIndex)
        : 0;

    return {
      // Stable non-Nivoda base so isPlayable stays true when frames[] drive URLs.
      url:
        "prepared-turntable://" +
        (typeof normalized.assetKey === "string" && normalized.assetKey
          ? normalized.assetKey
          : "asset"),
      frame_count: frameCount,
      top_index: topIndex,
      frames: frames,
      __preparedTurntable: true,
    };
  }

  function isPlayableV360Media(media) {
    if (!media || typeof media !== "object") return false;
    if (media.frames && Array.isArray(media.frames) && media.frames.length > 0) {
      return true;
    }
    return typeof media.url === "string" && media.url.length > 0;
  }

  /**
   * Explicit viewer-open resolver.
   * @param {object} diamond mapped diamond (may include preparedTurntable)
   * @param {object|null} v360 existing Nivoda v360 from resolveDiamondMedia
   * @param {object} [options]
   * @param {AbortSignal} [options.signal]
   * @param {typeof fetch} [options.fetch] test inject
   * @returns {Promise<object|null>} prepared media, or original v360, or null
   */
  function resolveViewerMedia(diamond, v360, options) {
    options = options || {};
    var fallback = v360 || null;
    var Loader = global.DiamondPreparedTurntableLoader;

    if (
      !Loader ||
      typeof Loader.isPreparedTurntableAvailable !== "function" ||
      typeof Loader.loadPreparedTurntableManifest !== "function"
    ) {
      return Promise.resolve(fallback);
    }

    var preparedMeta = diamond && diamond.preparedTurntable;
    if (!Loader.isPreparedTurntableAvailable(preparedMeta)) {
      return Promise.resolve(fallback);
    }

    var loadOpts = {};
    if (options.signal) loadOpts.signal = options.signal;
    if (options.fetch) loadOpts.fetch = options.fetch;

    return Loader.loadPreparedTurntableManifest(preparedMeta, loadOpts).then(
      function (result) {
        var media = normalizedManifestToMedia(result);
        return media || fallback;
      },
      function () {
        // Loader is soft-fail; this is defensive only.
        return fallback;
      }
    );
  }

  /**
   * Same as resolveViewerMedia, plus a stale-activation guard.
   * When isStale() is true after the async load, returns
   * { stale: true, media: null } so callers must not apply the result.
   *
   * @param {object} diamond
   * @param {object|null} v360
   * @param {object} [options]
   * @param {function(): boolean} [options.isStale]
   * @returns {Promise<{stale:boolean, media:object|null}>}
   */
  function resolveViewerMediaForActivation(diamond, v360, options) {
    options = options || {};
    var isStale =
      typeof options.isStale === "function" ? options.isStale : function () {
        return false;
      };

    return resolveViewerMedia(diamond, v360, options).then(function (media) {
      if (isStale()) {
        return { stale: true, media: null };
      }
      return { stale: false, media: media };
    });
  }

  /**
   * Whether viewer open should enter the frame-engine path for this diamond.
   * True when Nivoda v360 exists and/or prepared metadata is available.
   * Never fetches.
   */
  function canAttemptFrameViewer(diamond, v360) {
    if (v360 && typeof v360.url === "string" && v360.url.length > 0) {
      return true;
    }
    var Loader = global.DiamondPreparedTurntableLoader;
    if (
      Loader &&
      typeof Loader.isPreparedTurntableAvailable === "function" &&
      Loader.isPreparedTurntableAvailable(diamond && diamond.preparedTurntable)
    ) {
      return true;
    }
    return false;
  }

  global.DiamondPreparedTurntableViewer = {
    resolveViewerMedia: resolveViewerMedia,
    resolveViewerMediaForActivation: resolveViewerMediaForActivation,
    normalizedManifestToMedia: normalizedManifestToMedia,
    canAttemptFrameViewer: canAttemptFrameViewer,
    isPlayableV360Media: isPlayableV360Media,
  };
})(typeof window !== "undefined" ? window : globalThis);
